// =================================================================================
// File: src/components/Map/MudoWorldMap.jsx
// Description: Beautiful branded world map using Leaflet with custom Mudo pins
// =================================================================================

import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

// Import custom pin SVGs
import studioPin from "../../assets/map-pin-mudo.svg";
import workshopPin from "../../assets/map-pin-workshop.svg";
import supplierPin from "../../assets/map-pin-supplier.svg";

// =================================================================================
// Custom Marker Icons
// =================================================================================

const createCustomIcon = (type) => {
  const iconUrls = {
    studio: studioPin,
    workshop: workshopPin,
    supplier: supplierPin,
    other: studioPin,
  };

  return L.icon({
    iconUrl: iconUrls[type] || iconUrls.studio,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42],
    shadowUrl: null,
    className: "mudo-map-marker",
  });
};

// =================================================================================
// Map Controller Component
// =================================================================================

function MapController({ locations, initialCenter, initialZoom, disableAutoFit }) {
  const map = useMap();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (disableAutoFit || !locations || locations.length === 0 || hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    if (locations.length === 1) {
      const loc = locations[0];
      map.setView([loc.lat, loc.lng], initialZoom || 6, { animate: true, duration: 1.0 });
    } else {
      const bounds = L.latLngBounds(locations.map((loc) => [loc.lat, loc.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: initialZoom || 12, animate: true, duration: 1.0 });
    }
  }, [locations, map, initialZoom, disableAutoFit]);

  return null;
}

// =================================================================================
// Main MudoWorldMap Component
// =================================================================================

export default function MudoWorldMap({
  locations = [],
  initialCenter = [20, 0],
  initialZoom = 2,
  onLocationClick,
  disableAutoFit = false,
  // worldMode: "studio" or "visitor" - controls pin click behavior
  // studio: opens website_url in new tab
  // visitor: navigates to studio page using slug or id
  worldMode = "visitor",
  className = "",
  style = {},
}) {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const markerClusterGroupRef = useRef(null);

  const validLocations = (locations || []).filter(
    (loc) =>
      loc &&
      typeof loc.lat === "number" &&
      typeof loc.lng === "number" &&
      !isNaN(loc.lat) &&
      !isNaN(loc.lng)
  );

  // Setup marker clustering when map is ready
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;

    // cleanup old group (hot reload safety)
    if (markerClusterGroupRef.current) {
      try {
        map.removeLayer(markerClusterGroupRef.current);
      } catch (_) {}
      markerClusterGroupRef.current = null;
    }

    const markerClusterGroup = L.markerClusterGroup({
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        let size = "small";
        let bgColor = "#C17A5F";
        if (count >= 10) {
          size = "large";
          bgColor = "#8B5A44";
        } else if (count >= 5) {
          size = "medium";
          bgColor = "#A86F5A";
        }

        return L.divIcon({
          html: `<div style="
            width: ${size === "large" ? "48px" : size === "medium" ? "40px" : "32px"};
            height: ${size === "large" ? "48px" : size === "medium" ? "40px" : "32px"};
            display:flex;align-items:center;justify-content:center;
            background:${bgColor};
            border:3px solid rgba(255,255,255,0.9);
            border-radius:50%;
            color:white;font-weight:700;
            font-size:${size === "large" ? "16px" : size === "medium" ? "14px" : "12px"};
            box-shadow:0 4px 12px rgba(0,0,0,0.3);
            font-family:'Inter', system-ui, sans-serif;
          ">${count}</div>`,
          className: "mudo-marker-cluster",
          iconSize: L.point(
            size === "large" ? 48 : size === "medium" ? 40 : 32,
            size === "large" ? 48 : size === "medium" ? 40 : 32
          ),
        });
      },
    });

    markerClusterGroupRef.current = markerClusterGroup;
    map.addLayer(markerClusterGroup);

    return () => {
      try {
        if (markerClusterGroupRef.current && map) map.removeLayer(markerClusterGroupRef.current);
      } catch (_) {}
    };
  }, [mapReady]);

  // Add markers to cluster group
  useEffect(() => {
    if (!markerClusterGroupRef.current || !mapReady) return;

    const clusterGroup = markerClusterGroupRef.current;
    clusterGroup.clearLayers();

    console.log("[MudoWorldMap] Creating markers for locations:", validLocations.length, validLocations);

    validLocations.forEach((location) => {
      const icon = createCustomIcon(location.type || "studio");
      const marker = L.marker([location.lat, location.lng], { icon });
      
      console.log("[MudoWorldMap] Added marker for:", location.name, "ID:", location.id);

      const popupContent = `
        <div style="font-family:'Inter',system-ui,sans-serif;min-width:180px;padding:8px 4px;">
          <div style="font-size:14px;font-weight:600;color:#1f2937;margin-bottom:4px;">
            ${location.name || "Unnamed Location"}
          </div>
          ${
            location.address
              ? `<div style="font-size:11px;color:#6b7280;margin-bottom:6px;line-height:1.4;">📍 ${location.address}</div>`
              : ""
          }
          ${
            location.description
              ? `<div style="font-size:12px;color:#6b7280;margin-bottom:8px;line-height:1.4;">${location.description}</div>`
              : ""
          }
          <div style="display:flex;align-items:center;gap:8px;margin-top:10px;">
            <span style="
              display:inline-block;font-size:10px;padding:2px 8px;border-radius:12px;
              background:rgba(193,122,95,0.15);color:#8B5A44;font-weight:600;
              text-transform:uppercase;letter-spacing:0.5px;
            ">${location.type || "studio"}</span>

            <button
              type="button"
              data-mudo-open-studio="1"
              style="
                margin-left:auto;
                padding:8px 10px;
                border-radius:12px;
                border:none;
                background:#C17A5F;
                color:#fff;
                font-size:12px;
                font-weight:700;
                cursor:pointer;
                box-shadow:0 6px 16px rgba(0,0,0,0.18);
              "
            >Open</button>
          </div>
        </div>
      `;

      marker.bindPopup(popupContent, {
        maxWidth: 280,
        className: "mudo-popup",
        closeButton: true,
      });

      // Build target URL based on world mode and clicked pin data
      let targetUrl = null;
      
      if (worldMode === "studio") {
        // Studio world: open website in new tab
        targetUrl = location.website_url || location.website;
      } else {
        // Visitor world: navigate to studio page using slug or id
        const identifier = location.slug || location.id || location.studio_id || location.studioId;
        if (identifier) {
          targetUrl = `/discover/studios/${encodeURIComponent(String(identifier))}`;
        }
      }

      console.log("[MudoWorldMap] Marker setup - location:", location.name, "worldMode:", worldMode, "targetUrl:", targetUrl);

      const go = (ev) => {
        if (!targetUrl) {
          console.warn("[MudoWorldMap] No targetUrl, cannot navigate");
          return;
        }

        // stop map drag from swallowing the event
        try {
          ev?.preventDefault?.();
          ev?.stopPropagation?.();
        } catch (_) {}

        console.log("[MudoWorldMap] ✅ MARKER EVENT FIRED! Location:", location.name, "worldMode:", worldMode);

        if (onLocationClick) {
          try {
            console.log("[MudoWorldMap] Calling onLocationClick callback");
            onLocationClick(location);
          } catch (err) {
            console.error("[MudoWorldMap] onLocationClick callback error:", err);
          }
        }

        // Studio world: open in new tab. Visitor world: navigate in same window
        if (worldMode === "studio") {
          console.log("[MudoWorldMap] Opening external URL in new tab:", targetUrl);
          window.open(targetUrl, "_blank", "noopener,noreferrer");
        } else {
          console.log("[MudoWorldMap] Executing window.location.assign():", targetUrl);
          window.location.assign(targetUrl);
        }
      };

      // Leaflet click (desktop usually)
      marker.on("click", go);

      // DOM fallback with multi-event capture (mobile + clustered markers)
      marker.on("add", () => {
        const el = marker.getElement?.();
        console.log("[MudoWorldMap] marker.on('add') - setup multi-event capture for:", location.name);

        if (!el || !targetUrl) {
          console.warn("[MudoWorldMap] Skipping multi-event - missing element or targetUrl");
          return;
        }

        el.style.pointerEvents = "auto";
        el.style.cursor = "pointer";

        // ✅ capture = true so overlays/leaflet don't intercept first
        const opts = { capture: true, passive: false };

        el.addEventListener("pointerdown", go, opts);
        el.addEventListener("touchstart", go, opts);
        el.addEventListener("mousedown", go, opts);
        el.addEventListener("click", go, opts);

        console.log("[MudoWorldMap] Multi-event handlers attached (pointerdown, touchstart, mousedown, click)");

        marker.on("remove", () => {
          try {
            el.removeEventListener("pointerdown", go, opts);
            el.removeEventListener("touchstart", go, opts);
            el.removeEventListener("mousedown", go, opts);
            el.removeEventListener("click", go, opts);
            console.log("[MudoWorldMap] Multi-event handlers removed");
          } catch (_) {}
        });
      });

      // ✅ Popup button also navigates (for touch devices)
      marker.on("popupopen", () => {
        console.log("[MudoWorldMap] Popup opened for:", location.name);
        
        const el = marker.getPopup()?.getElement?.();
        if (!el) {
          console.warn("[MudoWorldMap] No popup element");
          return;
        }
        
        const btn = el.querySelector?.('[data-mudo-open-studio="1"]');
        if (!btn) {
          console.warn("[MudoWorldMap] No Open button in popup");
          return;
        }
        
        btn.onclick = (e) => {
          console.log("[MudoWorldMap] ✅ POPUP BUTTON CLICKED! Location:", location.name);
          e.preventDefault();
          e.stopPropagation();
          console.log("[MudoWorldMap] Popup button executing navigation:", targetUrl);
          
          // Studio world: open in new tab. Visitor world: navigate in same window
          if (worldMode === "studio") {
            window.open(targetUrl, "_blank", "noopener,noreferrer");
          } else {
            window.location.assign(targetUrl);
          }
        };
        
        console.log("[MudoWorldMap] Popup button handler attached");
      });

      clusterGroup.addLayer(marker);
    });
  }, [validLocations, mapReady]);

  return (
    <div
      className={`mudo-world-map-wrapper ${className}`}
      style={{ position: "relative", width: "100%", height: "100%", ...style }}
    >
      <MapContainer
        ref={mapRef}
        center={initialCenter}
        zoom={initialZoom}
        scrollWheelZoom={true}
        tap={false}
        style={{ width: "100%", height: "100%", borderRadius: "inherit", zIndex: 1 }}
        zoomControl={true}
        whenCreated={(map) => {
          mapRef.current = map;
          setMapReady(true);
        }}
        whenReady={() => setMapReady(true)}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          maxZoom={19}
        />

        <MapController
          locations={validLocations}
          initialCenter={initialCenter}
          initialZoom={initialZoom}
          disableAutoFit={disableAutoFit}
        />
      </MapContainer>

      {!mapReady && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0, 0, 0, 0.05)",
            backdropFilter: "blur(8px)",
            borderRadius: "inherit",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #C17A5F",
              borderTop: "4px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .mudo-popup .leaflet-popup-content-wrapper {
          border-radius: 16px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          border: 1px solid rgba(193,122,95,0.2);
          padding: 4px;
        }
        .mudo-popup .leaflet-popup-tip {
          background: white;
          border: 1px solid rgba(193,122,95,0.2);
        }
        .mudo-map-marker { transition: transform 0.2s ease; cursor: pointer; }
        .mudo-map-marker:hover { transform: scale(1.1); filter: drop-shadow(0 4px 8px rgba(193,122,95,0.4)); }
        .leaflet-marker-icon,
        .leaflet-marker-icon * {
          pointer-events: auto !important;
        }
      `}</style>
    </div>
  );
}
